import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { CurrencyEuroIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useCompanyData } from '../hooks/useCompanyData';
import { useCompany } from '../contexts/CompanyContext';
import type { Database } from '../types/database.types';

type CostFormData = Omit<Database['public']['Tables']['costs']['Insert'], 'employee_id'>;

export default function CostEntry() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<CostFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      description: '',
    }
  });
  const queryClient = useQueryClient();

  const { companyId, isLoading: isLoadingCompanyId } = useCompany();
  const { data: companyData, isLoading: isLoadingData, isError } = useCompanyData(companyId);

  // Haal employee_id op van ingelogde user
  const { data: currentEmployeeId } = useQuery({
    queryKey: ['currentEmployeeId'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const { data, error } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data.id;
    },
    enabled: !!companyId,
    staleTime: Infinity,
  });

  const projects = companyData?.projects || [];

  const createMutation = useMutation({
    mutationFn: async (data: CostFormData) => {
      if (!companyId) throw new Error('Geen bedrijf gevonden');
      if (!currentEmployeeId) throw new Error('Geen employee ID gevonden');
      if (!data.project_id) throw new Error('Project is verplicht – selecteer een project.');

      // Security check: verify project belongs to company
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('id', data.project_id)
        .eq('company_id', companyId)
        .single();

      if (projectError || !project) {
        throw new Error('Ongeldig of niet-toegankelijk project geselecteerd');
      }

      const costData = {
        ...data,
        company_id: companyId,
        employee_id: currentEmployeeId, // KRITIEK: employee_id meesturen
        amount: Number(data.amount),
        description: data.description?.trim() || null,
      };

      const { error } = await supabase.from('costs').insert([costData]);
      if (error) throw new Error(`Fout bij opslaan: ${error.message}`);
      return costData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData', companyId] });
      toast.success('💰 Kosten succesvol opgeslagen');
      reset();
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Er is een fout opgetreden');
    }
  });

  const onSubmit = (data: CostFormData) => createMutation.mutate(data);

  const isLoading = isLoadingCompanyId || isLoadingData || !currentEmployeeId;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Gegevens laden...</span>
        </div>
      </div>
    );
  }

  if (isError || !companyId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200">Fout bij laden van gegevens</h3>
              <p className="text-red-600 dark:text-red-300 mt-1">
                {!companyId ? 'Geen bedrijfsgegevens gevonden.' : 'Er is een fout opgetreden bij het laden van de bedrijfsgegevens.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Bedrijfsgegevens laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <CurrencyEuroIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Kosten Registratie</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Registreer kosten voor je projecten</p>
          </div>
        </div>
        <div className="max-w-2xl">
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Type *</label>
                <select
                  {...register('type', { required: 'Type is verplicht' })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Selecteer type</option>
                  <option value="material">Materiaal</option>
                  <option value="transport">Transport</option>
                  <option value="equipment">Apparatuur</option>
                  <option value="labor">Arbeid</option>
                  <option value="other">Overig</option>
                </select>
                {errors.type && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.type.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bedrag *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">€</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    {...register('amount', {
                      required: 'Bedrag is verplicht',
                      valueAsNumber: true,
                      min: { value: 0.01, message: 'Bedrag moet groter zijn dan 0' }
                    })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-8 pr-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                {errors.amount && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.amount.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project *</label>
                <select
                  {...register('project_id', { required: 'Project is verplicht' })}
                  disabled={projects.length === 0}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50"
                >
                  <option value="">Selecteer project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {errors.project_id && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.project_id.message}</p>}
                {projects.length === 0 && (
                  <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
                    Geen projecten beschikbaar. Vraag je admin om projecten aan te maken.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Datum *</label>
                <input
                  type="date"
                  {...register('date', { required: 'Datum is verplicht' })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                {errors.date && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Omschrijving</label>
                <textarea
                  {...register('description')}
                  rows={3}
                  placeholder="Optionele beschrijving van de kosten"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => reset()}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || isSubmitting || projects.length === 0}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-800 transition-colors"
                >
                  {createMutation.isPending ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Opslaan...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <CurrencyEuroIcon className="h-4 w-4 mr-2" />
                      Kosten Opslaan
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}